import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  ChevronRight, ArrowLeft, CheckCircle2, Play, Star, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { interviewModules, getInterviewModule } from '../data/interviewModules';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InterviewModuleDetail() {
  const { moduleId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState({ completed_lessons: [] });
  const [loading, setLoading] = useState(true);

  const module = getInterviewModule(moduleId);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/cabin-training/progress`, { withCredentials: true });
      setProgress(response.data || { completed_lessons: [] });
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return (progress?.completed_lessons || []).includes(lessonId);
  };

  const getUnitProgress = (unit) => {
    const completed = unit.lessons?.filter(l => isLessonCompleted(l.lesson_id)).length || 0;
    const total = unit.lessons?.length || 1;
    return Math.round((completed / total) * 100);
  };

  const getModuleProgress = () => {
    if (!module) return 0;
    let completed = 0;
    let total = 0;
    module.units?.forEach(unit => {
      unit.lessons?.forEach(lesson => {
        total++;
        if (isLessonCompleted(lesson.lesson_id)) {
          completed++;
        }
      });
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-500 to-pink-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">🎯</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg">Modül bulunamadı</p>
          <button 
            onClick={() => navigate('/interview-prep')} 
            className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  const moduleProgress = getModuleProgress();

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className={`bg-gradient-to-br ${module.color} px-4 pt-6 pb-8 rounded-b-[2rem] relative overflow-hidden`}>
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => navigate('/interview-prep')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Geri</span>
            </button>
            
            <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
              <Star className="w-4 h-4 text-yellow-300" />
              <span className="text-white font-bold text-sm">{moduleProgress}%</span>
            </div>
          </div>

          <div className="flex items-center gap-4 text-white">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-lg">
              {module.icon}
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Modül {moduleId}</p>
              <h1 className="text-2xl font-bold">{module.title}</h1>
              <p className="text-white/80 text-sm mt-1">{module.subtitle}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 bg-white/20 backdrop-blur rounded-xl p-3">
            <div className="flex items-center justify-between text-white text-sm mb-2">
              <span>İlerleme</span>
              <span>{moduleProgress}%</span>
            </div>
            <Progress value={moduleProgress} className="h-2 bg-white/30" />
          </div>
        </div>
      </div>

      {/* Units List */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600" />
          Üniteler
        </h2>
        
        <div className="space-y-4">
          {module.units?.map((unit, index) => {
            const unitProgress = getUnitProgress(unit);
            const isCompleted = unitProgress === 100;
            
            return (
              <Card key={unit.id} className="border-0 shadow-md overflow-hidden">
                <CardContent className="p-0">
                  {/* Unit Header */}
                  <div className={`p-4 border-b ${isCompleted ? 'bg-green-50' : 'bg-white'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          isCompleted 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <span className="font-bold">{index + 1}</span>
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-800">{unit.title}</h3>
                          <p className="text-sm text-gray-500">{unit.lessons?.length || 0} Ders</p>
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                        {unitProgress}%
                      </span>
                    </div>
                    
                    {!isCompleted && unitProgress > 0 && (
                      <Progress value={unitProgress} className="h-1.5 mt-3" />
                    )}
                  </div>
                  
                  {/* Lessons */}
                  <div className="divide-y divide-gray-100">
                    {unit.lessons?.map((lesson, lessonIndex) => {
                      const completed = isLessonCompleted(lesson.lesson_id);
                      return (
                        <button
                          key={lesson.lesson_id}
                          onClick={() => navigate(`/cabin-training/lesson/${lesson.lesson_id}`)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              completed 
                                ? 'bg-green-100' 
                                : 'bg-purple-100'
                            }`}>
                              {completed ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <Play className="w-4 h-4 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <span className={`font-medium ${completed ? 'text-green-700' : 'text-gray-800'}`}>
                                {lesson.title}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="w-4 h-4" />
                              <span className="text-sm font-medium">{lesson.xp || 20}</span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
